import './scss/index.scss';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import Backend from 'i18next-http-backend';
import { DateTime } from 'luxon';
import React from 'react';
import ReactDOM from 'react-dom/client';
import ReactModal from 'react-modal';
import { RecoilRoot } from 'recoil';
import { RouterProvider } from 'react-router-dom';
import { environment } from './environment';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import reportWebVitals from './reportWebVitals';
import router from './routes';

declare global {
  interface Window {
    _mtm?: Array<Record<string, unknown>>;
  }
}

console.log(environment());

const setPageTitle = (title: string) => {
  document.title = title;
};

const setPageLang = (lang: string) => {
  document.documentElement.lang = lang;
};

const initializeMatomo = () => {
  window._mtm = window._mtm || [];
  window._mtm.push({
    'mtm.startTime': new Date().getTime(),
    event: 'mtm.Start',
  });
  (function () {
    var d = document,
      g = d.createElement('script'),
      s = d.getElementsByTagName('script')[0];
    g.async = true;
    g.src = process.env.REACT_APP_MATOMO_URL!;
    s.parentNode!.insertBefore(g, s);
  })();
};

const setMetaPageDescription = (description: string) => {
  let element = document.querySelector("meta[name='description']");
  if (element) {
    element.setAttribute('content', description);
  }
};

const setManifestHref = (lang: string) => {
  const manifest = document.querySelector("link[rel='manifest']");
  if (manifest) {
    manifest.setAttribute('href', `${process.env.PUBLIC_URL}/${lang}/site.webmanifest?v=2`);
  }
};

const queryClient = new QueryClient();

const isLocaleFormatOptions = (value: unknown): value is Intl.DateTimeFormatOptions => {
  return typeof value === 'object' && value !== null;
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

localStorage.setItem(
  'lang',
  new URLSearchParams(window.location.search).get('lang') || navigator.language.split('-')[0]
);
i18n
  .use(Backend)
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    lng: new URLSearchParams(window.location.search).get('lang') || localStorage.getItem('lang')!,
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
      format: (value: unknown, format?: string) => {
        // legacy usage
        if (value instanceof Date) {
          const dateTimePreset = format
            ? (DateTime as unknown as Record<string, unknown>)[format]
            : undefined;

          return DateTime.fromJSDate(value)
            .setZone('Europe/Brussels')
            .toLocaleString(
              isLocaleFormatOptions(dateTimePreset) ? dateTimePreset : DateTime.DATETIME_SHORT
            );
        }
        return value == null ? '' : String(value);
      },
    },
  })
  .then(() => {
    setPageTitle(i18n.t('app.title'));
    setMetaPageDescription(i18n.t('app.description'));
    setPageLang(i18n.language);
    setManifestHref(i18n.language);
    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <RecoilRoot>
            <RouterProvider router={router} />
          </RecoilRoot>
        </QueryClientProvider>
      </React.StrictMode>
    );
    ReactModal.setAppElement('#root');
    initializeMatomo();
  });

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
